import React, { useEffect, useMemo, useState } from "react";
import { Card, Table, Tag, Input, Select, Space, Modal, Button, Empty } from "antd";
import { SearchOutlined, HistoryOutlined } from "@ant-design/icons";
import useRequest from "../hooks/useRequest";
import useDebounce from "../hooks/useDebounce";

const { Option } = Select;

// Cross-client "all Assistant activity" feed for Admin (spec requirement 13
// — the broader reading, beyond the per-Client audit log already reachable
// from the Assistants tab on a Client's own detail page).
//
// Structure: Client -> Assistants (expandable rows) -> click an Assistant to
// open its full activity log in a modal. Two backend calls:
//   GET /admin/assistant-audit-log/summary  — one row per Assistant that has
//     ever logged an action, with its Client + activity count + last-seen.
//     Grouped by Client here on the frontend.
//   GET /admin/assistant-audit-log?assistantId=X — the detail feed for one
//     Assistant, opened in the modal.
const AssistantAuditLog = () => {
  const { request } = useRequest();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState(null);
  const debouncedSearch = useDebounce(search, 400);

  const [detailFor, setDetailFor] = useState(null); // { assistantId, assistantName, clientName }
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    request({
      url: "/admin/assistant-audit-log/summary",
      method: "GET",
      onSuccess: (res) => {
        setSummary(res.data?.summary || []);
        setLoading(false);
      },
      onError: () => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clientGroups = useMemo(() => {
    const bySearch = debouncedSearch
      ? summary.filter((row) => {
          const needle = debouncedSearch.toLowerCase();
          return (
            row.assistantName?.toLowerCase().includes(needle) ||
            row.client?.name?.toLowerCase().includes(needle) ||
            row.client?.email?.toLowerCase().includes(needle)
          );
        })
      : summary;

    const groups = new Map();
    bySearch.forEach((row) => {
      const clientId = row.parentClientId;
      if (!groups.has(clientId)) {
        groups.set(clientId, {
          key: clientId,
          clientId,
          clientName: row.client?.name || "Unknown Client",
          clientEmail: row.client?.email,
          assistants: [],
        });
      }
      groups.get(clientId).assistants.push(row);
    });

    return Array.from(groups.values()).map((g) => ({
      ...g,
      assistantCount: g.assistants.length,
      lastActivity: g.assistants.reduce(
        (max, a) => (a.lastActivity && (!max || a.lastActivity > max) ? a.lastActivity : max),
        null
      ),
    }));
  }, [summary, debouncedSearch]);

  const openAssistantDetail = (row) => {
    setDetailFor({
      assistantId: row.assistantId,
      assistantName: row.assistantName,
      clientName: row.client?.name,
    });
    setDetailLoading(true);
    const params = new URLSearchParams({ assistantId: row.assistantId, limit: "100" });
    if (resultFilter) params.set("result", resultFilter);
    request({
      url: `/admin/assistant-audit-log?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => {
        setDetailLogs(res.data?.logs || []);
        setDetailLoading(false);
      },
      onError: () => setDetailLoading(false),
    });
  };

  const clientColumns = [
    { title: "Client", dataIndex: "clientName", width: 260 },
    { title: "Email", dataIndex: "clientEmail", width: 260, render: (v) => v || "—" },
    { title: "Assistants", dataIndex: "assistantCount", width: 140, align: "center" },
    {
      title: "Last Activity",
      dataIndex: "lastActivity",
      width: 220,
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
  ];

  const assistantColumns = [
    { title: "Assistant", dataIndex: "assistantName", width: 260 },
    { title: "Total Actions", dataIndex: "totalActions", width: 160, align: "center" },
    {
      title: "Last Activity",
      dataIndex: "lastActivity",
      width: 220,
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
    {
      title: "",
      width: 160,
      render: (_, row) => (
        <Button size="small" icon={<HistoryOutlined />} onClick={() => openAssistantDetail(row)}>
          View Activity
        </Button>
      ),
    },
  ];

  const detailColumns = [
    { title: "Action", dataIndex: "action" },
    { title: "Resource", dataIndex: "resourceType" },
    {
      title: "Result",
      dataIndex: "result",
      align: "center",
      render: (result) => <Tag color={result === "success" ? "green" : "red"}>{result}</Tag>,
    },
    { title: "IP", dataIndex: "ipAddress" },
    {
      title: "Time",
      dataIndex: "created_at",
      render: (v) => (v ? new Date(v).toLocaleString() : "—"),
    },
  ];

  return (
    <>
      <Card
        title="Assistant Activity"
        bodyStyle={{ padding: 0 }}
        headStyle={{ paddingTop: 8, paddingBottom: 8 }}
        extra={
          <Space align="center" size={12}>
            <Input
              placeholder="Search by Client or Assistant"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 280 }}
              size="middle"
            />
            <Select
              placeholder="Result"
              allowClear
              style={{ width: 140 }}
              size="middle"
              value={resultFilter}
              onChange={setResultFilter}
            >
              <Option value="success">Success</Option>
              <Option value="denied">Denied</Option>
            </Select>
          </Space>
        }
      >
        <Table
          rowKey="key"
          loading={loading}
          dataSource={clientGroups}
          columns={clientColumns}
          style={{ padding: "0 4px" }}
          locale={{ emptyText: <Empty description="No Assistant activity yet" /> }}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                rowKey="assistantId"
                dataSource={record.assistants}
                columns={assistantColumns}
                pagination={false}
                size="small"
              />
            ),
          }}
        />
      </Card>

      {detailFor && (
        <Modal
          title={`${detailFor.assistantName} — ${detailFor.clientName || ""}`}
          open
          onCancel={() => setDetailFor(null)}
          footer={null}
          width={800}
          centered
        >
          <Table
            rowKey="_id"
            loading={detailLoading}
            dataSource={detailLogs}
            columns={detailColumns}
            pagination={{ pageSize: 10 }}
            scroll={{ x: "max-content" }}
          />
        </Modal>
      )}
    </>
  );
};

export default AssistantAuditLog;
